import { useMemo } from "react";

import { useLoadedSessionId } from "./useLoadedSessionId";
import { useResetConfiguration } from "./useResetConfiguration";
import { useSessionParams } from "./useSessionParams";

export interface UseSessionResult {
  sessionId?: string;
  language?: string;
  error?: Error;
  isLoading: boolean;
  reset: () => Promise<{ configID: string; language: string } | undefined>;
  isResetDisabled: boolean;
}

/**
 * A composite hook for unified session management with "last action wins" behavior
 *
 * @description Combines session parameters, loaded session ID management, and reset functionality
 * into a single interface. Implements intelligent precedence logic where the most recent action
 * determines the active session ID. All state management is now handled by individual hooks.
 *
 * @param params - Optional URL parameters for initial session configuration
 * @param id - Optional configuration ID for loading a saved session
 * @returns Object containing:
 *   - sessionId: The currently active session ID (determined by precedence rules)
 *   - language: The language associated with the current session
 *   - error: Any error from the most recent session operation
 *   - isLoading: Combined loading state from all session operations
 *   - reset: Async function to reset configuration to initial state. Returns a Promise with the new configID and language
 *   - isResetDisabled: Boolean indicating if reset is currently disabled (20s cooldown after successful reset)
 *
 * @example
 * ```typescript
 * // Basic usage
 * const { sessionId, language, isLoading } = useSession();
 *
 * // With parameters
 * const { sessionId, reset } = useSession("config-params", "load-id");
 * ```
 *
 * @remarks
 * **Precedence Rules (Last Action Wins):**
 * 1. **Reset Data**: If user has reset configuration, use reset configID
 * 2. **Loaded Data**: If user has loaded a saved configuration, use loaded sessionID
 * 3. **Base Data**: If neither above, use initial configID from session params
 */
export const useSession = (
  params?: string | undefined,
  id?: string | undefined,
): UseSessionResult => {
  const sessionParams = useSessionParams(params);
  const loadedSession = useLoadedSessionId(id);
  const resetConfig = useResetConfiguration();

  const { sessionId, language } = useMemo(() => {
    if (resetConfig.hasData) {
      return {
        sessionId: resetConfig.configID,
        language: resetConfig.language,
      };
    }

    if (loadedSession.hasData) {
      return {
        sessionId: loadedSession.sessionId,
        language: sessionParams.language, // Use base language for loaded sessions
      };
    }

    return {
      sessionId: sessionParams.configID,
      language: sessionParams.language,
    };
  }, [
    resetConfig.hasData,
    resetConfig.configID,
    resetConfig.language,
    loadedSession.hasData,
    loadedSession.sessionId,
    sessionParams.configID,
    sessionParams.language,
  ]);

  const error = useMemo(() => {
    return resetConfig.error || loadedSession.error || sessionParams.error;
  }, [resetConfig.error, loadedSession.error, sessionParams.error]);

  const isLoading = useMemo(() => {
    return (
      (resetConfig.isLoading ||
        loadedSession.isLoading ||
        sessionParams.isLoading) ??
      false
    );
  }, [resetConfig.isLoading, loadedSession.isLoading, sessionParams.isLoading]);

  return {
    sessionId,
    language,
    error,
    isLoading,
    reset: resetConfig.reset,
    isResetDisabled: resetConfig.isResetDisabled,
  };
};
